import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { usePayers } from '../../../context/PayersContext';
import ReactSelect from 'react-select';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Textarea,
    Typography
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';

const JobCardForm = ({ open, onClose, onSuccess, jobCard, cars }) => {
    const { token, user } = useAuth();
    const { payers, loading: payersLoading } = usePayers();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [formData, setFormData] = useState({
        plate_no: '',
        service_location: 'in_house',
        service_center_name: '',
        service_center_contact: '',
        opened_date: new Date().toISOString().split('T')[0],
        mileage_at_service: '',
        diagnosis: '',
        work_performed: '',
        mechanic_name: '',
        external_mechanic_name: '',
        paid_by: 'OFFICE',
        input_by: user?.username || ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [carDetails, setCarDetails] = useState({ driver_name: '', owner_name: '', current_mileage: null, last_mileage_date: null });
    const [loadingCarDetails, setLoadingCarDetails] = useState(false);

    useEffect(() => {
        if (jobCard) {
            setFormData({
                plate_no: jobCard.plate_no || '',
                service_location: jobCard.service_location || 'in_house',
                service_center_name: jobCard.service_center_name || '',
                service_center_contact: jobCard.service_center_contact || '',
                opened_date: jobCard.opened_date ? new Date(jobCard.opened_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                mileage_at_service: jobCard.mileage_at_service || '',
                diagnosis: jobCard.diagnosis || '',
                work_performed: jobCard.work_performed || '',
                mechanic_name: jobCard.mechanic_name || '',
                external_mechanic_name: jobCard.external_mechanic_name || '',
                paid_by: jobCard.paid_by || 'OFFICE',
                input_by: user?.username || ''
            });
        } else {
            setFormData({
                plate_no: '',
                service_location: 'in_house',
                service_center_name: '',
                service_center_contact: '',
                opened_date: new Date().toISOString().split('T')[0],
                mileage_at_service: '',
                diagnosis: '',
                work_performed: '',
                mechanic_name: '',
                external_mechanic_name: '',
                paid_by: 'OFFICE',
                input_by: user?.username || ''
            });
        }
        setErrors({});
    }, [jobCard, user]);

    // Fetch car details when plate_no changes
    useEffect(() => {
        const fetchCarDetails = async () => {
            if (!formData.plate_no) {
                setCarDetails({ driver_name: '', owner_name: '', current_mileage: null, last_mileage_date: null });
                return;
            }

            try {
                setLoadingCarDetails(true);
                const response = await axios.get(
                    `${rootUrl}/cars/${encodeURIComponent(formData.plate_no)}`,
                    { headers: { 'Authorization': `Bearer ${authToken}` } }
                );

                if (response.data) {
                    setCarDetails({
                        driver_name: response.data.driver_name || 'Not Assigned',
                        owner_name: response.data.owner_name || 'Not Assigned',
                        current_mileage: response.data.current_mileage ?? null,
                        last_mileage_date: response.data.last_mileage_date || null
                    });
                }
            } catch (error) {
                console.error('Error fetching car details:', error);
                setCarDetails({ driver_name: 'Error loading', owner_name: 'Error loading', current_mileage: null, last_mileage_date: null });
            } finally {
                setLoadingCarDetails(false);
            }
        };

        fetchCarDetails();
    }, [formData.plate_no, authToken]);

    // Server enforces full date-aware ordering; client only flags new, non-backdated readings below the last one
    const getMileageError = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw) return 'Mileage at service is required';
        if (!/^\d+$/.test(raw) || parseInt(raw, 10) <= 0) return 'Mileage must be a positive whole number (km)';
        const { current_mileage, last_mileage_date } = carDetails;
        const isBackdated = last_mileage_date && formData.opened_date < String(last_mileage_date).split('T')[0];
        if (!jobCard && current_mileage != null && !isBackdated && parseInt(raw, 10) < current_mileage) {
            return `Mileage cannot go backwards — last recorded was ${Number(current_mileage).toLocaleString()} km`;
        }
        return null;
    };

    const formatMileageHint = () => {
        if (!formData.plate_no || loadingCarDetails) return null;
        if (carDetails.current_mileage == null) return 'No mileage on record for this vehicle';
        const date = carDetails.last_mileage_date
            ? new Date(carDetails.last_mileage_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : 'unknown date';
        return `Last recorded: ${Number(carDetails.current_mileage).toLocaleString()} km on ${date}`;
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.plate_no) newErrors.plate_no = 'Plate number is required';
        if (!formData.service_location) newErrors.service_location = 'Service location is required';
        if (formData.service_location === 'external' && !formData.service_center_name) {
            newErrors.service_center_name = 'Service center name is required for external services';
        }
        if (!formData.opened_date) newErrors.opened_date = 'Opened date is required';
        const mileageError = getMileageError(formData.mileage_at_service);
        if (mileageError) newErrors.mileage_at_service = mileageError;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setLoading(true);
        try {
            if (jobCard) {
                // Update existing job card
                const response = await axios.put(
                    `${rootUrl}/api/job-cards/${jobCard.id}`,
                    {
                        ...formData,
                        updated_by: user?.username || ''
                    },
                    { headers: { 'Authorization': `Bearer ${authToken}` } }
                );

                if (response.data && response.data.success) {
                    onSuccess && onSuccess(response.data.data);
                    onClose();
                    // Auto reload page
                    window.location.reload();
                } else {
                    setErrors({ submit: response.data?.message || 'Failed to update job card' });
                }
            } else {
                // Create new job card
                const response = await axios.post(
                    `${rootUrl}/api/job-cards`,
                    formData,
                    { headers: { 'Authorization': `Bearer ${authToken}` } }
                );

                if (response.data && response.data.success) {
                    onSuccess && onSuccess(response.data.data);
                    onClose();
                    // Auto reload page
                    window.location.reload();
                } else {
                    setErrors({ submit: response.data?.message || 'Failed to create job card' });
                }
            }
        } catch (error) {
            console.error('Error saving job card:', error);
            setErrors({
                submit: error.response?.data?.message || 'An error occurred while saving'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} handler={onClose} size="lg">
            <DialogHeader>
                <Typography variant="h5">
                    {jobCard ? 'Edit Job Card' : 'New Job Card'}
                </Typography>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4">
                    {errors.submit && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {errors.submit}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Plate Number *
                            </label>
                            <ReactSelect
                                options={cars ? cars.map((car) => ({
                                    value: car.plate_no,
                                    label: `${car.plate_no} - ${car.make} ${car.model}`
                                })) : []}
                                value={formData.plate_no ? {
                                    value: formData.plate_no,
                                    label: cars?.find(c => c.plate_no === formData.plate_no)
                                        ? `${formData.plate_no} - ${cars.find(c => c.plate_no === formData.plate_no).make} ${cars.find(c => c.plate_no === formData.plate_no).model}`
                                        : formData.plate_no
                                } : null}
                                onChange={(option) => setFormData({ ...formData, plate_no: option?.value || '' })}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select plate number"
                                isClearable
                            />
                            {errors.plate_no && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.plate_no}
                                </Typography>
                            )}
                        </div>

                        {/* Driver - Readonly */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Driver
                            </label>
                            <Input
                                value={loadingCarDetails ? 'Loading...' : carDetails.driver_name}
                                readOnly
                                className="bg-gray-100 cursor-not-allowed"
                            />
                        </div>

                        {/* Owner - Readonly */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Owner
                            </label>
                            <Input
                                value={loadingCarDetails ? 'Loading...' : carDetails.owner_name}
                                readOnly
                                className="bg-gray-100 cursor-not-allowed"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Service Location *
                            </label>
                            <ReactSelect
                                options={[
                                    { value: 'in_house', label: 'In-House' },
                                    { value: 'external', label: 'External' }
                                ]}
                                value={formData.service_location ? {
                                    value: formData.service_location,
                                    label: formData.service_location === 'in_house' ? 'In-House' : 'External'
                                } : null}
                                onChange={(option) => setFormData({ ...formData, service_location: option?.value || '' })}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select service location"
                            />
                            {errors.service_location && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.service_location}
                                </Typography>
                            )}
                        </div>

                        {formData.service_location === 'external' && (
                            <>
                                <div>
                                    <Input
                                        label="Service Center Name *"
                                        value={formData.service_center_name}
                                        onChange={(e) => setFormData({ ...formData, service_center_name: e.target.value })}
                                        error={!!errors.service_center_name}
                                    />
                                    {errors.service_center_name && (
                                        <Typography variant="small" color="red" className="mt-1">
                                            {errors.service_center_name}
                                        </Typography>
                                    )}
                                </div>

                                <div>
                                    <Input
                                        label="Service Center Contact"
                                        value={formData.service_center_contact}
                                        onChange={(e) => setFormData({ ...formData, service_center_contact: e.target.value })}
                                    />
                                </div>
                            </>
                        )}

                        <div>
                            <Input
                                type="date"
                                label="Opened Date *"
                                value={formData.opened_date}
                                onChange={(e) => setFormData({ ...formData, opened_date: e.target.value })}
                                error={!!errors.opened_date}
                            />
                            {errors.opened_date && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.opened_date}
                                </Typography>
                            )}
                        </div>

                        <div>
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                label="Mileage at Service (km) *"
                                className="text-right"
                                value={formData.mileage_at_service}
                                onChange={(e) => setFormData({ ...formData, mileage_at_service: e.target.value })}
                                error={!!errors.mileage_at_service}
                            />
                            {errors.mileage_at_service ? (
                                <Typography variant="small" className="mt-1 text-red-500">
                                    {errors.mileage_at_service}
                                </Typography>
                            ) : formatMileageHint() && (
                                <Typography variant="small" color="gray" className="mt-1">
                                    {formatMileageHint()}
                                </Typography>
                            )}
                        </div>

                        {formData.service_location === 'in_house' ? (
                            <div>
                                <Input
                                    label="Mechanic Name"
                                    value={formData.mechanic_name}
                                    onChange={(e) => setFormData({ ...formData, mechanic_name: e.target.value })}
                                />
                            </div>
                        ) : (
                            <div>
                                <Input
                                    label="External Mechanic Name"
                                    value={formData.external_mechanic_name}
                                    onChange={(e) => setFormData({ ...formData, external_mechanic_name: e.target.value })}
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Paid By
                            </label>
                            <ReactSelect
                                options={payers.map(p => ({ value: p.code, label: p.name }))}
                                value={formData.paid_by && payers.find(p => p.code === formData.paid_by) ? {
                                    value: formData.paid_by,
                                    label: payers.find(p => p.code === formData.paid_by).name
                                } : null}
                                onChange={(option) => setFormData({ ...formData, paid_by: option?.value || 'OFFICE' })}
                                isDisabled={payersLoading}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder={payersLoading ? "Loading payers..." : "Select paid by"}
                            />
                        </div>
                    </div>

                    <div>
                        <Textarea
                            label="Diagnosis"
                            value={formData.diagnosis}
                            onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                            rows={3}
                        />
                    </div>

                    <div>
                        <Textarea
                            label="Work Performed"
                            value={formData.work_performed}
                            onChange={(e) => setFormData({ ...formData, work_performed: e.target.value })}
                            rows={3}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        color="red"
                        onClick={onClose}
                        className="mr-1"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="green"
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default JobCardForm;

